import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { camelizeKeys, decamelizeKeys } from "humps";

import LocalStorage from "../services/LocalStorage";

const API_BASE_URL_MAP = {
  quine: `${process.env.NEXT_PUBLIC_BACKEND_DOMAIN}/`,
  stackexchange: `https://api.stackexchange.com/2.3/`,
  topicMapLambda: `https://fykq23g7jh.execute-api.eu-west-1.amazonaws.com/`,
} as const;

export type TApiType = keyof typeof API_BASE_URL_MAP | null;

type Options = {
  authed?: boolean;
  api?: TApiType;
  shouldCamelizeKeys?: boolean;
};

export const createFetcher = (options?: Options): AxiosInstance => {
  const {
    authed = true,
    api = "quine",
    shouldCamelizeKeys = true,
  } = options || {};
  const token = LocalStorage.getAccessToken();
  const headers: AxiosRequestConfig["headers"] = {
    accept: "application/json",
    "Content-type": "application/json",
  };
  if (api !== "stackexchange" && authed && token) {
    headers.authorization = `Bearer ${token}`;
  }
  const client = axios.create({
    baseURL: api ? API_BASE_URL_MAP[api] : undefined,
    headers,
  });

  const _handleRequestConfig = (
    config: InternalAxiosRequestConfig
  ): InternalAxiosRequestConfig => {
    if (config.data) {
      config.data = decamelizeKeys(config.data);
    }
    if (config.params) {
      config.params = decamelizeKeys(config.params);
    }
    return config;
  };

  const _handleResponse = <T>(axiosResponse: AxiosResponse<T>) => {
    if (axiosResponse.data && shouldCamelizeKeys) {
      axiosResponse.data = camelizeKeys(axiosResponse.data) as unknown as T;
    }
    return axiosResponse;
  };

  const _handleError = <T>(error: T) => Promise.reject(error);

  client.interceptors.response.use(_handleResponse, _handleError);
  client.interceptors.request.use(_handleRequestConfig, _handleError);

  return client;
};
